import { CircleAlert, CircleCheck, Loader2, RotateCw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { formatFileSize } from "../../utils/format-size.ts";
import type { UploadItem } from "../../utils/upload-queue.ts";
import { DocumentFileIcon } from "../document-file-icon";

const STATUS_LABELS: Record<UploadItem["status"], string> = {
	ready: "Pronto para enviar",
	rejected: "Recusado",
	queued: "Na fila",
	uploading: "Enviando",
	completing: "Confirmando",
	done: "Enviado",
	failed: "Falhou",
};

interface UploadQueueItemProps {
	item: UploadItem;
	onRetry: (id: string) => void;
	onRemove: (id: string) => void;
}

function statusText(item: UploadItem) {
	if (item.status === "uploading") return `Enviando ${item.progress}%`;
	return STATUS_LABELS[item.status];
}

export function UploadQueueItem({
	item,
	onRetry,
	onRemove,
}: UploadQueueItemProps) {
	const showProgress =
		item.status === "uploading" ||
		item.status === "completing" ||
		item.status === "queued";
	const removable =
		item.status === "ready" ||
		item.status === "rejected" ||
		item.status === "failed";

	return (
		<li className="upload-queue-item" data-status={item.status}>
			<DocumentFileIcon fileName={item.file.name} />
			<div className="upload-queue-item__body">
				<div className="upload-queue-item__header">
					<span className="upload-queue-item__name" title={item.file.name}>
						{item.file.name}
					</span>
					<span className="upload-queue-item__size">
						{formatFileSize(item.file.size)}
					</span>
				</div>
				{showProgress && (
					<Progress
						value={item.progress}
						aria-label={`Progresso de ${item.file.name}`}
						className="upload-queue-item__progress"
					/>
				)}
				<div className="upload-queue-item__status" role="status">
					{item.status === "done" && <CircleCheck />}
					{(item.status === "failed" || item.status === "rejected") && (
						<CircleAlert />
					)}
					{item.status === "completing" && (
						<Loader2 className="upload-queue-item__spinner" />
					)}
					<span>{item.error ?? statusText(item)}</span>
				</div>
			</div>
			<div className="upload-queue-item__actions">
				{item.status === "failed" && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label={`Tentar de novo ${item.file.name}`}
						onClick={() => onRetry(item.id)}
					>
						<RotateCw />
					</Button>
				)}
				{removable && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label={`Remover ${item.file.name} da lista`}
						onClick={() => onRemove(item.id)}
					>
						<X />
					</Button>
				)}
			</div>
		</li>
	);
}
